import axios from 'axios';
import fileDownload from 'react-file-download';
import i18next from 'i18next';
import { showToast } from 'stores/ToastStore';
import { groupBy } from 'lodash';
import { isProd } from 'helpers/helper_functions';
import SessionStore from 'stores/SessionStore';
import { getBearerGrantTypeAuthHeaders } from '../helpers/auth_helpers';

const groups = [
  {
    id: 1,
    name: 'Motor Carrier Operations',
  },
  {
    id: 2,
    name: 'Northwest',
    parentId: 1,
  },
  {
    id: 3,
    name: 'Southeast',
    parentId: 1,
  },
  {
    id: 4,
    name: 'Central',
    parentId: 1,
  },
  {
    id: 5,
    name: 'West',
    parentId: 1,
  },
  {
    id: 6,
    name: 'Customer Success',
  },
  {
    id: 7,
    name: 'Northeast',
    parentId: 6,
  },
  {
    id: 8,
    name: 'Southeast',
    parentId: 6,
  },
  {
    id: 9,
    name: 'Central',
    parentId: 6,
  },
  {
    id: 10,
    name: 'West',
    parentId: 6,
  },
  {
    id: 11,
    name: 'Carrier Success',
  },
  {
    id: 12,
    name: 'Account Management',
  },
  {
    id: 13,
    name: 'Finance',
  },
  {
    id: 14,
    name: 'Data Entry',
  },
  {
    id: 15,
    name: 'Other',
  },
];

export async function createReport(report) {
  const url = `${API_HOST}/api/filters`;

  try {
    const response = await axios({
      method: 'POST',
      data: report,
      url,
      headers: {
        'Content-Type': 'application/json',
        Authorization: getBearerGrantTypeAuthHeaders(),
      },
    });

    showToast('Success!', 'The report has been created');

    return response.data;
  } catch (error) {
    console.error(error);
    showToast('Failed to create report', error.data?.error, 'error');

    return null;
  }
}

export async function getGroupsMock({ queryKey }) {
  const [, withRegions] = queryKey;
  return new Promise((resolve) => {
    setTimeout(() => {
      if (withRegions) {
        const grouped = groupBy(groups, 'parentId');
        const mapped = groups
          .filter((g) => !g.parentId)
          .map((group) => {
            return {
              ...group,
              children: grouped[group.id],
            };
          });

        resolve(mapped);
      } else {
        resolve(groups.filter((group) => !group.parentId));
      }
    }, 2000);
  });
}

export async function getGroups() {
  let url = `${API_HOST}/api/v2/filter-group`;

  try {
    const response = await axios({
      method: 'GET',
      url,
      headers: {
        'Content-Type': 'application/json',
        Authorization: getBearerGrantTypeAuthHeaders(),
      },
    });

    return response.data.data;
  } catch (error) {
    console.error(error);
    showToast('Failed to get groups', error.data?.error, 'error');

    return null;
  }
}

export async function getColumnBlacklist() {
  let url = `${API_HOST}/api/reporting/unifiedview/columns/blacklist`;

  try {
    const response = await axios({
      method: 'GET',
      url,
      headers: {
        'Content-Type': 'application/json',
        Authorization: getBearerGrantTypeAuthHeaders(),
      },
    });

    if (!response?.data?.blacklist) {
      showToast('Failed to get blacklist', '', 'error');
    }
    return response.data.blacklist;
  } catch (error) {
    showToast('Failed to get blacklist', error, 'error');
    return {};
  }
}

function findGroup(groupId) {
  const group = groups.find((group) => group.id === groupId);

  if (group?.parentId) {
    group.parent = groups.find((g) => g.id === group.parentId);
  }

  return group;
}

function findRegionsByGroup(groupId) {
  if (!groupId) {
    return [];
  }
  const mapped = parseInt(groupId, 10);
  console.log(groups);
  return groups.filter((group) => group.parentId === mapped);
}

export async function getRegionsByGroup({ queryKey }) {
  const [, groupId] = queryKey;
  const regions = findRegionsByGroup(groupId);
  console.log('regions: ', regions);

  return new Promise((resolve) => {
    resolve(regions);
  });
}
export async function getGroup({ queryKey }) {
  const [, groupId] = queryKey;

  const group = findGroup(groupId);

  return new Promise((resolve, reject) => {
    if (!group) {
      reject(new Error('Group not found'));
    } else {
      resolve(group);
    }
  });
}

const users = ['Abe Park', 'Jane Doe', 'John Smith', 'Ryan B', 'Janet J'];

let reports = groups.reduce((reports, group) => {
  return reports.concat(
    Array.from(Array(50).keys()).reduce((reports, i) => {
      const id = group.id + 100 * i;
      const parent = group.parentId
        ? groups.find((g) => g.id === group.parentId)
        : null;
      const createdBy = users[i % 5];
      return reports.concat({
        id,
        name: `Amazon Load Tenders - Report #${id}`,
        description:
          'This report is used by the Finance Team. We use this to see if there are any outstanding charges',
        groupId: group.id,
        group: parent || group,
        region: parent ? group : null,
        createdAt: Date.parse('2023-12-31'),
        createdBy,
        updatedAt: new Date(),
        isFavorite: i < 10 && group.id === 1,
        filters: [1, 2, 3],
        actionsDisabled: !['Abe Park', 'Jane Doe'].includes(createdBy),
      });
    }, []),
  );
}, []);

export async function deleteReport(report) {
  const url = `${API_HOST}/api/filters`;

  try {
    const response = await axios({
      method: 'DELETE',
      data: report,
      url,
      headers: {
        'Content-Type': 'application/json',
        Authorization: getBearerGrantTypeAuthHeaders(),
      },
    });

    showToast('Report has been deleted', null, 'success');

    return response.data.data;
  } catch (error) {
    console.error(error);
    showToast('Failed to delete report', error.data?.error, 'error');

    return null;
  }
}

export async function setFavoriteReport({ id, isFavorite }) {
  try {
    await axios({
      method: isFavorite ? 'POST' : 'DELETE',
      url: `${API_HOST}/api/v2/favorite-filters/${id}`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: getBearerGrantTypeAuthHeaders(),
      },
    });

    if (isFavorite) {
      showToast('Report has been added to favorites', null, 'success');
    } else {
      showToast('Report has been removed from favorites', null, 'success');
    }

    return { isSuccess: true };
  } catch (error) {
    console.error(error);
    showToast('Failed to set report favorite', error.data?.error, 'error');

    return { isSuccess: false };
  }
}

export async function updateReport(report) {
  const url = `${API_HOST}/api/filters`;

  try {
    const response = await axios({
      method: 'PUT',
      data: report,
      url,
      headers: {
        'Content-Type': 'application/json',
        Authorization: getBearerGrantTypeAuthHeaders(),
      },
    });

    showToast('Report has been updated', null, 'success');

    return response.data;
  } catch (error) {
    console.error(error);
    showToast('Failed to create report', error.data?.error, 'error');

    return null;
  }
}

function findReport(reportId) {
  const report = reports.find((report) => report.id === reportId);
  if (report) {
    report.group = findGroup(report?.groupId);
  }

  return report;
}

/**
 * Retrieves customer reports based on the provided customer ID. The function
 * returns a different response depending on the environment.
 * In non-production environments, it returns a stubbed array of reports.
 * In production environments, it returns reports specific to the provided customer ID.
 *
 * @param {string|number} id - The customer ID for which reports are to be retrieved.
 * @returns {Array} An array of reports for the customer. Returns an empty array if no reports are found.
 */
export function getCustomerReports() {
  if (!isProd()) {
    // Return a stubbed array of all reports if not in production
    return new Promise((resolve) => resolve(CUSTOMER_REPORTS ?? []));
  }
  const masterId = SessionStore.getMasterAccountId();
  return new Promise((resolve) => resolve(CUSTOMER_REPORTS[masterId] ?? []));
}

export async function getReport({ queryKey }) {
  const [, reportId] = queryKey;
  const url = `${API_HOST}/api/filters/${reportId}`;

  try {
    const response = await axios({
      method: 'GET',
      url,
      headers: {
        'Content-Type': 'application/json',
        Authorization: getBearerGrantTypeAuthHeaders(),
      },
    });

    return response.data;
  } catch (error) {
    console.error(error);
    showToast('Failed to create report', error.data?.error, 'error');

    return null;
  }
}

export async function getReports({ queryKey }) {
  const [
    ,
    {
      groupId,
      page,
      perPage,
      favorites,
      search,
      sortKey,
      sortOrder,
      isMyReports,
      lean,
    },
  ] = queryKey;

  let url;

  if (favorites) {
    url = `${API_HOST}/api/v2/favorite-filters`;
  } else if (isMyReports) {
    url = `${API_HOST}/api/v2/filters/my`;
  } else {
    url = `${API_HOST}/api/v2/filters`;
  }

  try {
    const response = await axios({
      method: 'GET',
      url,
      params: {
        groupId,
        page,
        lean,
        page_size: perPage,
        ...(search && { search }),
        ...(sortKey && {
          sortKey,
          sortOrder,
        }),
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: getBearerGrantTypeAuthHeaders(),
      },
    });

    return {
      items: response.data.data.filters,
      total: response.data.data.totalCount,
    };
  } catch (error) {
    console.error(error);
    showToast('Failed to get reports', error.data?.error, 'error');

    return null;
  }
}

export async function getReportsMock({ queryKey }) {
  const [, { groupId, page, perPage, favorites, search }] = queryKey;

  return new Promise((resolve) => {
    setTimeout(() => {
      let items = reports;

      if (groupId) {
        items = items.filter((report) => report.groupId === groupId);
      }

      if (favorites) {
        items = items.filter((report) => report.isFavorite);
      }

      if (search) {
        items = items.filter((report) => report.name.includes(search));
      }
      const total = items.length;

      const skip = (page - 1) * perPage;
      const paginatedItems = items.slice(skip, page * perPage);

      resolve({
        items: paginatedItems,
        total,
      });
    }, 1000);
  });
}

export async function getShipmentsByStream(body, requestOptions = {}) {
  try {
    const { params } = body;
    body.params = updateFilterDatesInParams(params);

    const response = await axios({
      method: 'POST',
      url: `${API_HOST}/api/reporting/unifiedview`,
      data: body,
      headers: {
        'Content-Type': 'application/json',
        Authorization: getBearerGrantTypeAuthHeaders(),
      },
      signal: requestOptions.controller.signal,
    }).catch((response) => {
      showToast(
        'Failed to get uv data',
        i18next.t('FAILED', { error: response.entity.error }),
        'error',
      );
      return null;
    });

    if (!response) {
      return {
        items: [],
        stats: { empty_delivery: 0, loaded_delivery: 0, loaded_pickup: 0 },
      };
    }

    const {
      data: {
        results: { items, stats },
      },
    } = response;

    return { items, stats };
  } catch (e) {
    return [];
  }
}

export async function getShipmentsCount(body, requestOptions = {}) {
  try {
    const { params } = body;
    body.params = updateFilterDatesInParams(params);

    const response = await axios({
      method: 'POST',
      url: `${API_HOST}/api/reporting/unifiedview/count`,
      data: body,
      headers: {
        'Content-Type': 'application/json',
        Authorization: getBearerGrantTypeAuthHeaders(),
      },
      signal: requestOptions.controller.signal,
    }).catch((response) => {
      showToast(
        'Failed to count uv data',
        i18next.t('FAILED', { error: response.entity.error }),
        'error',
      );
      return null;
    });

    if (!response) {
      return 0;
    }

    return response.data.count;
  } catch (e) {
    return 0;
  }
}

export async function getUVCsvExport(body) {
  const { params } = body;
  body.params = updateFilterDatesInParams(params);

  // Blob downloads don't work with our rest client. Axios `responseType` needed.
  const response = await axios({
    method: 'POST',
    url: `${API_HOST}/api/reporting/unifiedview/csv`,
    data: body,
    responseType: 'blob',
    headers: {
      'Content-Type': 'application/json',
      Authorization: getBearerGrantTypeAuthHeaders(),
    },
  });
  fileDownload(response.data, 'export.csv', 'application/zip');
}

export async function getUniqueMarketCodes() {
  try {
    const response = await axios({
      method: 'GET',
      url: `${API_HOST}/api/1/markets/unique`,
      headers: {
        Authorization: getBearerGrantTypeAuthHeaders(),
      },
    });
    return response.data.marketCodes;
  } catch (e) {
    return [];
  }
}

export async function getContainerStatuses() {
  try {
    const response = await axios({
      method: 'GET',
      url: `${API_HOST}/api/reporting/unifiedview/container-statuses`,
      headers: {
        Authorization: getBearerGrantTypeAuthHeaders(),
      },
    });

    return Object.values(response.data.statuses);
  } catch (e) {
    return [];
  }
}

function updateFilterDatesInParams(params) {
  Object.keys(params).forEach((item) => {
    const { filterType, type } = params[item];

    // overriding custom time ranges back to standard
    if (type === 'timeSelectionInRange') {
      params[item].type = 'inRange';
    }
  });

  return params;
}

export async function sendReport({
  emails = [],
  subject,
  message,
  ...filterModel
}) {
  const data = {
    emails,
    subject,
    message,
    ...filterModel,
  };
  try {
    await axios({
      method: 'POST',
      url: `${API_HOST}/api/reporting/unifiedview/exports/send`,
      data,
      headers: {
        Authorization: getBearerGrantTypeAuthHeaders(),
      },
    });
    showToast(i18next.t('SEND_REPORT_SUCCESS'));
  } catch (e) {
    showToast(i18next.t('SEND_REPORT_FAIL'), i18next.t(e.message), 'error');
  }
}
